import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import AccountSettings from "@/pages/AccountSettings";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import Dashboard from "@/pages/Dashboard";
import NewRFQ from "@/pages/NewRFQ";
import RFQDetail from "@/pages/RFQDetail";
import MyRFQs from "@/pages/MyRFQs";
import AgentProfileEdit from "@/pages/AgentProfileEdit";
import Messages from "@/pages/Messages";
import AdminDashboard from "@/pages/AdminDashboard";
import Favourites from "@/pages/Favourites";
import AgentDetail from "@/pages/AgentDetail";
import PublicRFQ from "@/pages/PublicRFQ";
import StartNiche from "@/pages/StartNiche";
import StartChat from "@/pages/StartChat";
import SubCategorySelect from "@/pages/SubCategorySelect";
import Blueprint from "@/pages/Blueprint";
import Resources from "@/pages/Resources";
import About from "@/pages/About";
import HowItWorks from "@/pages/HowItWorks";
import TrustSafety from "@/pages/TrustSafety";
import Contact from "@/pages/Contact";
import BuyerProfile from "@/pages/BuyerProfile";
import Workflow from "@/pages/Workflow";

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.role === "unassigned") return <Navigate to="/select-role" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Detect OAuth session_id in URL fragment BEFORE rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/select-role" element={<RoleSelect />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/manufacturers/:agentId" element={<AgentDetail />} />
      <Route path="/rfqs/new" element={<ProtectedRoute roles={["buyer"]}><NewRFQ /></ProtectedRoute>} />
      <Route path="/buyer/profile" element={<ProtectedRoute roles={["buyer"]}><BuyerProfile /></ProtectedRoute>} />
      <Route path="/favourites" element={<ProtectedRoute roles={["buyer"]}><Favourites /></ProtectedRoute>} />
      <Route path="/rfqs" element={<ProtectedRoute><MyRFQs /></ProtectedRoute>} />
      <Route path="/rfqs/:rfqId" element={<ProtectedRoute><RFQDetail /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute roles={["agent"]}><AgentProfileEdit /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/messages/:otherUserId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/workflow" element={<ProtectedRoute roles={["buyer", "agent", "admin"]}><Workflow /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/p/rfq/:token" element={<PublicRFQ />} />
      <Route path="/start" element={<StartNiche />} />
      <Route path="/start/chat" element={<StartChat />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:resourceId" element={<Resources />} />
      <Route path="/onboarding/sub-category" element={<ProtectedRoute roles={["buyer"]}><SubCategorySelect /></ProtectedRoute>} />
      <Route path="/blueprint/:niche/:subCategory" element={<ProtectedRoute roles={["buyer"]}><Blueprint /></ProtectedRoute>} />
      <Route path="/about" element={<About />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/trust-safety" element={<TrustSafety />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
